import PublicLayout from "@/components/PublicLayout";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Cross, ArrowRight, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    if (!token) {
      setError("Missing or invalid reset link.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Your password has been updated.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <PublicLayout hideFooter>
      <section className="celestial-bg min-h-[calc(100vh-4rem)] grid place-items-center px-5 lg:px-8 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="inline-flex w-12 h-12 rounded-full bg-gradient-to-br from-brand-sky to-brand-sage text-white items-center justify-center mb-4 shadow-sm">
              <Cross className="w-5 h-5" strokeWidth={1.6} />
            </span>
            <h1 className="font-serif text-4xl tracking-tight text-brand-ink">Choose a new password.</h1>
            <p className="text-brand-quiet mt-1">A fresh start, in His mercy.</p>
          </div>

          {!token ? (
            <div className="bg-white rounded-2xl border border-brand-line p-7 text-center" data-testid="reset-no-token">
              <p className="text-brand-ink">This reset link is missing or invalid.</p>
              <Link
                to="/forgot-password"
                data-testid="reset-request-new"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-5 py-2.5 font-medium"
              >
                Request a new link
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-white rounded-2xl border border-brand-line p-7 space-y-4 soft-shadow"
              data-testid="reset-password-form"
            >
              <div>
                <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">
                  New password (≥ 6 characters)
                </label>
                <input
                  required
                  minLength={6}
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-brand-quiet mb-1.5">Confirm password</label>
                <input
                  required
                  minLength={6}
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="w-full rounded-xl bg-brand-base border border-brand-line focus:ring-2 focus:ring-brand-sky/40 focus:border-brand-sky outline-none px-4 py-3 text-brand-ink"
                />
              </div>

              {error && (
                <p
                  data-testid="reset-error-message"
                  className="text-sm text-[#9a4a3f] bg-[#fbeae6] border border-[#f1cfc6] rounded-lg px-3 py-2"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-button"
                className="w-full rounded-full bg-brand-sky text-white hover:bg-brand-skyDark transition-colors px-6 py-3 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Saving…" : (<>Save new password <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>)}
              </button>

              <p className="text-xs text-brand-muted text-center pt-1 inline-flex items-center justify-center gap-1.5 w-full">
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.7} />
                Reset links expire in 30 minutes and can be used once.
              </p>
            </form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
